// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";

import NavBar from "./components/NavBar";
import Footer from "./components/Footer";

// Supabase + Auth bits
import { supabase } from "./supabaseClient";
import AuthOnly from "./auth/AuthOnly";
import ProtectedByPathModal from "./auth/ProtectedByPathModal";

// Auth pages
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard"; // kept in case you still use it somewhere

// Account page (profile/plan/status)
import AccountPage from "./pages/AccountPage";

// Top-level
import HomePage from "./pages/HomePage";
import PricingPage from "./pages/PricingPage";
import Legal from "./pages/Legal";
import Contact from "./pages/Contact";
import ScheduleTutorialsPage from "./pages/ScheduleTutorialsPage";

/* ---------------- NASCAR — CUP ---------------- */
import CupProjections from "./pages/nascar/CupProjections";
import NascarRangeOfOutcomes from "./pages/nascar/NascarRangeOfOutcomes";
import CupOdds from "./pages/nascar/CupOdds";
import CupGfs from "./pages/nascar/CupGfs";
import CupPractice from "./pages/nascar/CupPractice";
import CupCheatSheets from "./pages/nascar/CupCheatSheets";
import CupOptimizer from "./pages/nascar/CupOptimizer";
import CupLineupLab from "./pages/nascar/CupLineupLab";
import CupContestSim from "./pages/nascar/NascarContestSimulator";
import CupData from "./pages/nascar/CupData";
import CupArticle from "./pages/nascar/CupArticle";
import NascarDataExplorer from "./pages/nascar/NascarDataExplorer";
import NascarTrackData from "./pages/nascar/NascarTrackData";
import NascarRaceHub from "./pages/nascar/NascarRaceHub";
import CupArticleIndex from "./pages/nascar/CupArticleIndex";
import F1Projections from "./pages/nascar/F1Projections";
import F1Optimizer from "./pages/nascar/F1Optimizer";
import NascarContestDashboard from "./pages/nascar/NascarContestDashboard";

/* ---------------- NASCAR — XFINITY ---------------- */
import XfProjections from "./pages/nascar/XfProjections";
import XfOdds from "./pages/nascar/XfOdds";
import XfGfs from "./pages/nascar/XfGfs";
import XfPractice from "./pages/nascar/XfPractice";
import XfCheatSheets from "./pages/nascar/XfCheatSheets";
import XfOptimizer from "./pages/nascar/XfOptimizer";
import XfLineupLab from "./pages/nascar/XfLineupLab";
import XfData from "./pages/nascar/XfData";

/* ---------------- NASCAR — TRUCKS ---------------- */
import TrucksProjections from "./pages/nascar/TrucksProjections";
import TrucksOdds from "./pages/nascar/TrucksOdds";
import TrucksGfs from "./pages/nascar/TrucksGfs";
import TrucksPractice from "./pages/nascar/TrucksPractice";
import TrucksCheatSheets from "./pages/nascar/TrucksCheatSheets";
import TrucksOptimizer from "./pages/nascar/TrucksOptimizer";
import TrucksLineupLab from "./pages/nascar/TrucksLineupLab";
import TrucksData from "./pages/nascar/TrucksData";

/* ---------------- NFL (CLASSIC) ---------------- */
import NflProjections from "./pages/nfl/NflProjections";
import NflSimulations from "./pages/nfl/NflSimulations";
import NflRangeOfOutcomes from "./pages/nfl/NflRangeOfOutcomes";
import NflStacks from "./pages/nfl/NflStacks";
import NflCheatSheets from "./pages/nfl/NflCheatSheets";
import NFLOptimizerPage from "./pages/nfl/NFLOptimizerPage";
import PlayerPool from "./pages/nfl/PlayerPool";
import NFLLineupLab from "./pages/nfl/NFLLineupLab";
import NflContestSimulator from "./pages/nfl/NflContestSimulator";

// position projections
import NflPosProjections from "./pages/nfl/NflPosProjections";

// position data
import NflPositionalData from "./pages/nfl/NflPositionalData";

// NEW: Advanced Stats
import NflAdvancedStats from "./pages/nfl/NflAdvancedStats";
import NflTeamDataDashboard from "./pages/nfl/NflTeamDataDashboard";
import NflWeather from "./pages/nfl/NflWeather";

/* ---------------- NFL (SHOWDOWN) ---------------- */
import NflProjectionsShowdown from "./pages/nfl/NflProjectionsShowdown";
import NFLShowdownOptimizer from "./pages/nfl/NFLShowdownOptimizer";

import NflPositionalDataShowdown from "./pages/nfl/NflPositionalDataShowdown";

import NflPosProjectionsShowdown from "./pages/nfl/NflPosProjectionsShowdown";
import NFLShowdownLineupLab from "./pages/nfl/NFLShowdownLineupLab";

// season-long fantasy football
import FantasyFootballRankings from "./pages/nfl/FantasyFootballRankings";

/* ---------------- MLB ---------------- */
import MlbProjections from "./pages/mlb/MlbProjections";
import MlbRangeOfOutcomes from "./pages/mlb/MlbRangeOfOutcomes";
import MlbPositionalProjections from "./pages/mlb/MlbPositionalProjections";
import MlbSimulations from "./pages/mlb/MlbSimulations";
import MlbStacks from "./pages/mlb/MlbStacks";
import MlbCheatSheet from "./pages/mlb/MlbCheatSheet";
import MlbPositionalData from "./pages/mlb/MlbPositionalData";
import MlbMatchups from "./pages/mlb/MlbMatchups";
import MlbWeather from "./pages/mlb/MlbWeather";
import MlbAdvancedStats from "./pages/mlb/MlbAdvancedStats";
import MLBOptimizerPage from "./pages/mlb/MLBOptimizerPage";

/* ---------------- NBA ---------------- */
import NbaData from "./pages/nba/NbaData";
import NbaProjections from "./pages/nba/NbaProjections";
import NbaCheatSheet from "./pages/nba/NbaCheatSheet";
import NbaOptimizerPage from "./pages/nba/NbaOptimizerPage";
import NbaAdvancedStats from "./pages/nba/NbaAdvancedStats";

function NotFound() {
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-2">Page not found</h1>
      <p className="text-gray-600">Check the URL or use the navigation.</p>
    </div>
  );
}

// Layout that gates all nested sports routes using path-based rules + modal
function SportsGateLayout() {
  return (
    <ProtectedByPathModal>
      <Outlet />
    </ProtectedByPathModal>
  );
}

export default function App() {
  // Track logged-in user (some components still read this prop)
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    // initial session
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
    });
    // listen for changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  return (
    <BrowserRouter>
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1">
          <Routes>
            {/* ---------- Public ---------- */}
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/info" element={<ScheduleTutorialsPage />} />
            <Route path="/faq" element={<Navigate to="/info" replace />} />

            {/* ---------- Public NASCAR articles ---------- */}
            <Route path="/nascar/research/articles" element={<CupArticleIndex />} />
            <Route path="/nascar/research/articles/:slug" element={<CupArticle />} />
            <Route path="/nascar/cup/articles" element={<CupArticleIndex />} />
            <Route path="/nascar/cup/articles/:slug" element={<CupArticle />} />

            {/* ---------- Auth ---------- */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />

            {/* ---------- Account (login-only, never plan-gated) ---------- */}
            <Route
              path="/account"
              element={
                <AuthOnly>
                  <AccountPage />
                </AuthOnly>
              }
            />
            {/* Back-compat */}
            <Route path="/dashboard" element={<Navigate to="/account" replace />} />

            {/* ---------- GATED SPORTS AREA (all nested routes obey gateConfig rules) ---------- */}
            <Route element={<SportsGateLayout />}>
              <Route path="/nascar" element={<Navigate to="/nascar/cup/dfs-projections" replace />} />
              <Route path="/nascar/hub" element={<Navigate to="/nascar/cup/dfs-projections" replace />} />

              <Route path="/nfl" element={<Navigate to="/nfl/classic/projections" replace />} />
              <Route path="/nfl/hub" element={<Navigate to="/nfl/classic/projections" replace />} />
              {/* ============ NASCAR → CUP ============ */}
              <Route path="/nascar/cup/dfs-projections" element={<CupProjections />} />
              <Route path="/nascar/cup/projections" element={<Navigate to="/nascar/cup/dfs-projections" replace />} />
              <Route path="/nascar/cup/odds" element={<CupOdds />} />
              <Route path="/nascar/cup/betting-sims" element={<CupOdds />} />
              <Route path="/nascar/cup/sims" element={<Navigate to="/nascar/cup/betting-sims" replace />} />
              <Route path="/nascar/cup/gfs" element={<CupGfs />} />
              <Route path="/nascar/cup/practice-data" element={<CupPractice />} />
              <Route path="/nascar/cup/practice" element={<Navigate to="/nascar/cup/practice-data" replace />} />
              <Route path="/nascar/cup/cheatsheets" element={<CupCheatSheets />} />
              <Route path="/nascar/cup/cheat-sheets" element={<Navigate to="/nascar/cup/cheatsheets" replace />} />
              <Route path="/nascar/cup/optimizer" element={<CupOptimizer />} />
              <Route path="/nascar/cup/lineup-lab" element={<CupLineupLab />} />
              <Route path="/nascar/cup/contest-sim" element={<Navigate to="/nascar/cup/contest-simulator" replace />} />
              <Route path="/nascar/cup/contest-simulator" element={<CupContestSim />} />
              <Route path="/nascar/cup/range-of-outcomes" element={<NascarRangeOfOutcomes series="cup" />} />
              <Route path="/nascar/cup/driver-data" element={<CupData />} />
              <Route path="/nascar/cup/data" element={<Navigate to="/nascar/cup/driver-data" replace />} />

              {/* ============ NASCAR → O'REILLY ============ */}
              <Route path="/nascar/oreilly/dfs-projections" element={<XfProjections />} />
              <Route path="/nascar/oreilly/projections" element={<Navigate to="/nascar/oreilly/dfs-projections" replace />} />
              <Route path="/nascar/oreilly/odds" element={<XfOdds />} />
              <Route path="/nascar/oreilly/betting-sims" element={<XfOdds />} />
              <Route path="/nascar/oreilly/sims" element={<Navigate to="/nascar/oreilly/betting-sims" replace />} />
              <Route path="/nascar/oreilly/gfs" element={<XfGfs />} />
              <Route path="/nascar/oreilly/practice-data" element={<XfPractice />} />
              <Route path="/nascar/oreilly/practice" element={<Navigate to="/nascar/oreilly/practice-data" replace />} />
              <Route path="/nascar/oreilly/cheatsheets" element={<XfCheatSheets />} />
              <Route path="/nascar/oreilly/optimizer" element={<XfOptimizer />} />
              <Route path="/nascar/oreilly/lineup-lab" element={<XfLineupLab />} />
              <Route path="/nascar/oreilly/contest-simulator" element={<CupContestSim />} />
              <Route path="/nascar/oreilly/range-of-outcomes" element={<NascarRangeOfOutcomes series="xfinity" />} />
              <Route path="/nascar/oreilly/driver-data" element={<XfData />} />

              {/* Legacy Xfinity redirects */}
              <Route path="/nascar/xfinity/dfs-projections" element={<Navigate to="/nascar/oreilly/dfs-projections" replace />} />
              <Route path="/nascar/xfinity/projections" element={<Navigate to="/nascar/oreilly/projections" replace />} />
              <Route path="/nascar/xfinity/odds" element={<Navigate to="/nascar/oreilly/odds" replace />} />
              <Route path="/nascar/xfinity/betting-sims" element={<Navigate to="/nascar/oreilly/betting-sims" replace />} />
              <Route path="/nascar/xfinity/sims" element={<Navigate to="/nascar/oreilly/betting-sims" replace />} />
              <Route path="/nascar/xfinity/gfs" element={<Navigate to="/nascar/oreilly/gfs" replace />} />
              <Route path="/nascar/xfinity/practice" element={<Navigate to="/nascar/oreilly/practice-data" replace />} />
              <Route path="/nascar/xfinity/practice-data" element={<Navigate to="/nascar/oreilly/practice-data" replace />} />
              <Route path="/nascar/xfinity/cheatsheets" element={<Navigate to="/nascar/oreilly/cheatsheets" replace />} />
              <Route path="/nascar/xfinity/cheat-sheets" element={<Navigate to="/nascar/oreilly/cheatsheets" replace />} />
              <Route path="/nascar/xfinity/optimizer" element={<Navigate to="/nascar/oreilly/optimizer" replace />} />
              <Route path="/nascar/xfinity/lineup-lab" element={<Navigate to="/nascar/oreilly/lineup-lab" replace />} />
              <Route path="/nascar/xfinity/contest-simulator" element={<Navigate to="/nascar/oreilly/contest-simulator" replace />} />
              <Route path="/nascar/xfinity/range-of-outcomes" element={<Navigate to="/nascar/oreilly/range-of-outcomes" replace />} />
              <Route path="/nascar/xfinity/data" element={<Navigate to="/nascar/oreilly/driver-data" replace />} />

              {/* ============ NASCAR → TRUCKS ============ */}
              <Route path="/nascar/trucks/dfs-projections" element={<TrucksProjections />} />
              <Route path="/nascar/trucks/projections" element={<Navigate to="/nascar/trucks/dfs-projections" replace />} />
              <Route path="/nascar/trucks/odds" element={<TrucksOdds />} />
              <Route path="/nascar/trucks/betting-sims" element={<TrucksOdds />} />
              <Route path="/nascar/trucks/sims" element={<Navigate to="/nascar/trucks/betting-sims" replace />} />
              <Route path="/nascar/trucks/gfs" element={<TrucksGfs />} />
              <Route path="/nascar/trucks/practice-data" element={<TrucksPractice />} />
              <Route path="/nascar/trucks/practice" element={<Navigate to="/nascar/trucks/practice-data" replace />} />
              <Route path="/nascar/trucks/cheatsheets" element={<TrucksCheatSheets />} />
              <Route path="/nascar/trucks/cheat-sheets" element={<Navigate to="/nascar/trucks/cheatsheets" replace />} />
              <Route path="/nascar/trucks/optimizer" element={<TrucksOptimizer />} />
              <Route path="/nascar/trucks/lineup-lab" element={<TrucksLineupLab />} />
              <Route path="/nascar/trucks/contest-simulator" element={<CupContestSim />} />
              <Route path="/nascar/trucks/range-of-outcomes" element={<NascarRangeOfOutcomes series="trucks" />} />
              <Route path="/nascar/trucks/driver-data" element={<TrucksData />} />
              <Route path="/nascar/trucks/data" element={<Navigate to="/nascar/trucks/driver-data" replace />} />

              {/* ============ NASCAR → RESEARCH & TOOLS / F1 ============ */}
              <Route path="/nascar/research/loop-data-explorer" element={<NascarDataExplorer />} />
              <Route path="/nascar/research/race-hub" element={<NascarRaceHub />} />
              <Route path="/nascar/research/analysis-lab" element={<Navigate to="/nascar/research/race-hub" replace />} />
              <Route path="/nascar/research/track-data" element={<NascarTrackData />} />
              <Route path="/nascar/research/contest-dashboard" element={<NascarContestDashboard />} />
              <Route path="/nascar/f1/projections" element={<F1Projections />} />
              <Route path="/nascar/f1/optimizer" element={<F1Optimizer />} />

              {/* Legacy research/F1 redirects */}
              <Route path="/nascar/cup/data-explorer" element={<Navigate to="/nascar/research/loop-data-explorer" replace />} />
              <Route path="/nascar/misc/track-data" element={<Navigate to="/nascar/research/track-data" replace />} />
              <Route path="/nascar/misc/contest-dashboard" element={<Navigate to="/nascar/research/contest-dashboard" replace />} />
              <Route path="/nascar/misc/f1" element={<Navigate to="/nascar/f1/projections" replace />} />
              <Route path="/nascar/misc/f1/optimizer" element={<Navigate to="/nascar/f1/optimizer" replace />} />

              {/* ============ NFL — CLASSIC ============ */}
              <Route path="/nfl/classic/projections" element={<NflProjections />} />
              <Route path="/nfl/classic/simulations" element={<NflSimulations />} />
              <Route path="/nfl/classic/range-of-outcomes" element={<NflRangeOfOutcomes />} />
              <Route
                path="/nfl/classic/sims"
                element={<Navigate to="/nfl/classic/simulations" replace />}
              />
              <Route path="/nfl/classic/stacks" element={<NflStacks />} />
              <Route path="/nfl/classic/top-plays" element={<NflCheatSheets />} />
              <Route path="/nfl/classic/cheatsheets" element={<PlayerPool />} />
              <Route path="/nfl/classic/optimizer" element={<NFLOptimizerPage />} />

              <Route
                    path="/nfl/classic/contest-simulator"
                    element={<NflContestSimulator />}
                  />

                  <Route
                    path="/nfl/contest-simulator"
                    element={
                      <Navigate to="/nfl/classic/contest-simulator" replace />
                    }
                  />

              <Route path="/nfl/classic/player-pool" element={<Navigate to="/nfl/classic/cheatsheets" replace />} />
              <Route
                    path="/nfl/classic/lineup-lab"
                    element={<NFLLineupLab />}
                  />
              <Route
                path="/nfl/classic/playerpool"
                element={<Navigate to="/nfl/classic/cheatsheets" replace />}
              />

              <Route
                path="/nfl/classic/player-projections"
                element={<NflPosProjections />}
              />
              
              <Route
                path="/nfl/classic/player-data/:pos?"
                element={<NflPositionalData />}
              />

              {/* Legacy positional URLs */}
              <Route
                path="/nfl/classic/positional-projections"
                element={<Navigate to="/nfl/classic/player-projections" replace />}
              />
              <Route
                path="/nfl/classic/positional-data/:pos?"
                element={<Navigate to="/nfl/classic/player-data/qb" replace />}
              />

              <Route path="/nfl/classic/advanced-stats" element={<NflAdvancedStats />} />
              <Route path="/nfl/classic/team-projections" element={<NflTeamDataDashboard />} />
              <Route path="/nfl/classic/team-data" element={<Navigate to="/nfl/classic/team-projections" replace />} />
              <Route path="/nfl/classic/team-dashboard" element={<Navigate to="/nfl/classic/team-projections" replace />} />
              <Route path="/nfl/classic/weather" element={<NflWeather />} />

              {/* legacy shortcuts */}
              <Route path="/nfl/optimizer" element={<Navigate to="/nfl/classic/optimizer" replace />} />
              <Route path="/nfl/classic" element={<Navigate to="/nfl/classic/projections" replace />} />
              <Route path="/nfl/projections" element={<Navigate to="/nfl/classic/projections" replace />} />
              <Route path="/nfl/dfs-projections" element={<Navigate to="/nfl/classic/projections" replace />} />

              {/* ============ NFL — SHOWDOWN ============ */}
              <Route path="/nfl/showdown/projections" element={<NflProjectionsShowdown />} />
              <Route path="/nfl/showdown/optimizer" element={<NFLShowdownOptimizer />} />
              <Route path="/nfl/showdown/lineup-lab" element={<NFLShowdownLineupLab />} />
              <Route
                path="/nfl/showdown/player-data/:pos"
                element={<NflPositionalDataShowdown />}
              />
              <Route
                path="/nfl/showdown/player-data"
                element={<Navigate to="/nfl/showdown/player-data/qb" replace />}
              />

              <Route
                path="/nfl/showdown/player-projections"
                element={<NflPosProjectionsShowdown />}
              />

              {/* Legacy positional URLs */}
              <Route
                path="/nfl/showdown/positional-projections"
                element={<Navigate to="/nfl/showdown/player-projections" replace />}
              />
              <Route
                path="/nfl/showdown/positional-data/:pos?"
                element={<Navigate to="/nfl/showdown/player-data/qb" replace />}
              />

              <Route path="/nfl/showdown" element={<Navigate to="/nfl/showdown/projections" replace />} />

              {/* ============ NFL — SEASON LONG ============ */}
              <Route
                path="/nfl/season-long/rankings"
                element={<FantasyFootballRankings />}
              />
              <Route
                path="/nfl/season-long"
                element={<Navigate to="/nfl/season-long/rankings" replace />}
              />

              {/* IMPORTANT: do NOT define /nfl here anymore (public hub owns /nfl) */}

              {/* ============ MLB ============ */}
              <Route path="/mlb/projections" element={<MlbProjections />} />
              <Route path="/mlb/range-of-outcomes" element={<MlbRangeOfOutcomes />} />
              <Route path="/mlb/player-projections" element={<MlbPositionalProjections />} />
              <Route
                path="/mlb/positional-projections"
                element={<Navigate to="/mlb/player-projections" replace />}
              />
              <Route path="/mlb/simulations" element={<MlbSimulations />} />
              <Route path="/mlb/batters" element={<Navigate to="/mlb/batter-projections" replace />} />
              <Route path="/mlb/hitters" element={<Navigate to="/mlb/batter-projections" replace />} />
              <Route path="/mlb/stacks" element={<MlbStacks />} />
              <Route path="/mlb/cheatsheets" element={<MlbCheatSheet />} />

              <Route path="/mlb/player-data" element={<MlbPositionalData />} />
              <Route
                path="/mlb/positional-data"
                element={<Navigate to="/mlb/player-data" replace />}
              />
              <Route path="/mlb/pitchers" element={<Navigate to="/mlb/pitcher-data" replace />} />
              <Route path="/mlb/lineups" element={<MlbMatchups />} />
              <Route path="/mlb/matchups" element={<Navigate to="/mlb/lineups" replace />} />
              <Route path="/mlb/weather" element={<MlbWeather />} />
              <Route path="/mlb/advanced-stats" element={<MlbAdvancedStats />} />
              <Route path="/mlb/optimizer" element={<MLBOptimizerPage />} />
              <Route path="/mlb" element={<Navigate to="/mlb/projections" replace />} />

              {/* ============ NBA (NEW) ============ */}
              <Route
                path="/nba/data"
                element={
                  <NbaData
                    title="NBA — All Players"
                    dataUrl="/data/nba/latest/data.json"
                    metaUrl="/data/nba/latest/meta.json"
                  />
                }
              />
              <Route path="/nba/projections" element={<NbaProjections />} />
              <Route path="/nba/optimizer" element={<NbaOptimizerPage />} />
              <Route path="/nba" element={<Navigate to="/nba/data" replace />} />
              <Route path="/nba/cheatsheets" element={<NbaCheatSheet />} />
              <Route path="/nba/advanced-stats" element={<NbaAdvancedStats />} />
            </Route>

            {/* ---------- 404 ---------- */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}
